import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
  WebAuthnError,
} from "@simplewebauthn/browser";
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from "@simplewebauthn/browser";

/**
 * Client-side orchestration of the WebAuthn / passkey ceremonies.
 *
 * Each helper drives the two-leg ceremony against the server endpoints exposed
 * by the WebAuthn backend foundation: fetch options, hand them to the
 * authenticator via `@simplewebauthn/browser`, then POST the
 * attestation/assertion back for verification.
 */

const AUTHENTICATION_OPTIONS_URL = "/api/auth/webauthn/authentication/options";
const AUTHENTICATION_VERIFY_URL = "/api/auth/webauthn/authentication/verify";
const REGISTRATION_OPTIONS_URL = "/api/auth/webauthn/registration/options";
const REGISTRATION_VERIFY_URL = "/api/auth/webauthn/registration/verify";

/** Shape of the JSON error returned by the WebAuthn ceremony endpoints. */
type WebauthnErrorBody = {
  error?: string;
  error_description?: string;
};

/**
 * Normalized passkey failure. `cancelled` is set when the user dismissed the
 * native prompt (or it timed out) — callers typically treat that as a no-op
 * rather than a hard error.
 */
export class PasskeyError extends Error {
  readonly cancelled: boolean;

  constructor(message: string, cancelled = false) {
    super(message);
    this.name = "PasskeyError";
    this.cancelled = cancelled;
  }
}

/** Whether this browser exposes the WebAuthn API at all. */
export function isPasskeySupported(): boolean {
  return typeof window !== "undefined" && browserSupportsWebAuthn();
}

/**
 * Whether we should offer to *create* a passkey here: requires WebAuthn support
 * and a usable platform authenticator (Touch ID, Windows Hello, Android, …).
 * Used to suppress the post-login "add a passkey" prompt on devices that can't
 * meaningfully act on it.
 */
export async function canRegisterPasskey(): Promise<boolean> {
  if (!isPasskeySupported()) return false;
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
}

function isUserCancellation(error: unknown): boolean {
  if (!(error instanceof WebAuthnError)) return false;
  if (error.code === "ERROR_CEREMONY_ABORTED") return true;
  // A user-dismissed native prompt surfaces as a passed-through NotAllowedError.
  const cause = error.cause;
  return cause instanceof Error && cause.name === "NotAllowedError";
}

async function postJson<T>(url: string, body?: unknown): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const payload = (await response.json().catch(() => null)) as T | WebauthnErrorBody | null;

  if (!response.ok) {
    const message =
      (payload as WebauthnErrorBody | null)?.error_description ??
      "Something went wrong with your passkey. Please try again.";
    throw new PasskeyError(message);
  }

  if (payload === null) {
    throw new PasskeyError("Received an empty response from the server.");
  }

  return payload as T;
}

type AuthenticationVerifyResult = { verified: boolean; redirectTo: string };

/**
 * Run the authentication ceremony end-to-end. On success returns the path the
 * caller should navigate to for the now-authenticated session.
 */
export async function signInWithPasskey(): Promise<AuthenticationVerifyResult> {
  const optionsJSON = await postJson<PublicKeyCredentialRequestOptionsJSON>(
    AUTHENTICATION_OPTIONS_URL,
  );

  let assertion: AuthenticationResponseJSON;
  try {
    assertion = await startAuthentication({ optionsJSON });
  } catch (error) {
    if (isUserCancellation(error)) {
      throw new PasskeyError("Passkey sign-in was cancelled.", true);
    }
    throw new PasskeyError(
      error instanceof Error ? error.message : "Could not use a passkey to sign in.",
    );
  }

  return postJson<AuthenticationVerifyResult>(AUTHENTICATION_VERIFY_URL, {
    response: assertion,
  });
}

type RegistrationVerifyResult = { verified: boolean; credentialId: string };

/**
 * Run the registration ceremony end-to-end for the signed-in user, optionally
 * labelling the new credential.
 */
export async function registerPasskey(name?: string): Promise<RegistrationVerifyResult> {
  const optionsJSON = await postJson<PublicKeyCredentialCreationOptionsJSON>(
    REGISTRATION_OPTIONS_URL,
  );

  let attestation: RegistrationResponseJSON;
  try {
    attestation = await startRegistration({ optionsJSON });
  } catch (error) {
    if (isUserCancellation(error)) {
      throw new PasskeyError("Passkey setup was cancelled.", true);
    }
    throw new PasskeyError(
      error instanceof Error ? error.message : "Could not create a passkey.",
    );
  }

  return postJson<RegistrationVerifyResult>(REGISTRATION_VERIFY_URL, {
    response: attestation,
    name,
  });
}
